import { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import PasswordInput, { isPasswordValid } from '../components/PasswordInput';

export default function ClientSettings() {
  const { user } = useAuth();
  const [form, setForm] = useState({ report_email: '', report_frequency: 'daily', report_enabled: false });
  const [emails, setEmails] = useState(['']);
  const [theme, setTheme] = useState({ theme_primary: '#3b82f6', theme_secondary: '#1a2744' });
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/auth/me').then(r => {
      const emailStr = r.data.report_email || r.data.email || '';
      const emailList = emailStr.split(',').map(e => e.trim()).filter(Boolean);
      setEmails(emailList.length ? emailList : ['']);
      setForm({
        report_email: emailStr,
        report_frequency: r.data.report_frequency || 'daily',
        report_enabled: !!r.data.report_enabled
      });
    });
  }, []);

  async function handleSaveSettings(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const validEmails = emails.filter(e => e.trim());
      await api.put('/clients/me/settings', { ...form, report_email: validEmails.join(',') });
      toast.success('Nustatymai išsaugoti');
    } catch {
      toast.error('Klaida');
    } finally {
      setSaving(false);
    }
  }


  async function handleSaveTheme(e) {
    e.preventDefault();
    try {
      await api.put('/clients/me/theme', theme);
      document.documentElement.style.setProperty('--accent', theme.theme_primary);
      toast.success('Tema išsaugota');
    } catch { toast.error('Klaida'); }
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirm) { toast.error('Slaptažodžiai nesutampa'); return; }
    try {
      await api.put('/auth/password', pwForm);
      toast.success('Slaptažodis pakeistas');
      setPwForm({ currentPassword: '', newPassword: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Klaida');
    }
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Nustatymai</div>
        <div className="page-subtitle">Ataskaitų ir paskyros nustatymai</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
        <div className="card">
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>📧 Automatinės ataskaitos</div>
          <form onSubmit={handleSaveSettings}>
            <div className="form-group">
              <label className="form-label">Ataskaitų el. pašto adresai</label>
              {emails.map((email, i) => (
                <div key={i} style={{ display:'flex', gap:8, marginBottom:8 }}>
                  <input
                    className="form-input" type="email"
                    value={email}
                    onChange={e => { const n=[...emails]; n[i]=e.target.value; setEmails(n); }}
                    placeholder="ataskaitos@imone.lt"
                    style={{ flex:1 }}
                  />
                  {emails.length > 1 && (
                    <button type="button" onClick={() => setEmails(emails.filter((_,j)=>j!==i))}
                      style={{ padding:'0 12px', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:8, color:'#ef4444', cursor:'pointer', fontSize:16 }}>
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={() => setEmails([...emails, ''])}
                style={{ fontSize:12, color:'var(--accent)', background:'none', border:'none', cursor:'pointer', padding:'4px 0', display:'flex', alignItems:'center', gap:4 }}>
                ＋ Pridėti el. pašto adresą
              </button>
              <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:6 }}>Ataskaitos bus siunčiamos į visus nurodytus adresus</div>
            </div>

            <div className="form-group">
              <label className="form-label">Siuntimo periodiškumas</label>
              <select className="form-select" value={form.report_frequency} onChange={e => setForm({...form, report_frequency: e.target.value})}>
                <option value="hourly">Kas valandą</option>
                <option value="daily">Kasdien (07:00 ryto)</option>
                <option value="weekly">Kas savaitę (pirmadienį)</option>
                <option value="monthly">Kas mėnesį (1-ą dieną)</option>
              </select>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '12px 16px', background: 'var(--card)', borderRadius: 8, border: '1px solid var(--border)' }}>
                <input type="checkbox" checked={form.report_enabled} onChange={e => setForm({...form, report_enabled: e.target.checked})} style={{ width: 16, height: 16 }} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>Įjungti automatinį siuntimą</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Ataskaitos bus siunčiamos automatiškai nurodytu periodiškumu</div>
                </div>
              </label>
            </div>

            <button className="btn btn-primary" type="submit" disabled={saving} style={{ width: '100%', justifyContent: 'center' }}>
              {saving ? <span className="loading-spinner" /> : '💾 Išsaugoti nustatymus'}
            </button>
          </form>
        </div>

        <div className="card">
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>🎨 Portalo spalvos</div>
          <form onSubmit={handleSaveTheme}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Pagrindinė spalva</label>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <input type="color" value={theme.theme_primary}
                    onChange={e => setTheme({...theme, theme_primary: e.target.value})}
                    style={{ width:48, height:40, borderRadius:8, border:'1px solid var(--border)', cursor:'pointer', padding:2, background:'none' }} />
                  <input className="form-input" value={theme.theme_primary}
                    onChange={e => setTheme({...theme, theme_primary: e.target.value})}
                    placeholder="#3b82f6" style={{ flex:1 }} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Fono spalva</label>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <input type="color" value={theme.theme_secondary}
                    onChange={e => setTheme({...theme, theme_secondary: e.target.value})}
                    style={{ width:48, height:40, borderRadius:8, border:'1px solid var(--border)', cursor:'pointer', padding:2, background:'none' }} />
                  <input className="form-input" value={theme.theme_secondary}
                    onChange={e => setTheme({...theme, theme_secondary: e.target.value})}
                    placeholder="#1a2744" style={{ flex:1 }} />
                </div>
              </div>
            </div>
            <div style={{ padding:'12px 16px', borderRadius:10, marginBottom:12,
              background: theme.theme_secondary, border:'1px solid var(--border)' }}>
              <div style={{ fontSize:12, color:'#fff', marginBottom:6, opacity:0.7 }}>Peržiūra:</div>
              <button type="button" style={{ padding:'8px 16px', borderRadius:8, border:'none',
                background: theme.theme_primary, color:'#fff', fontSize:13, fontWeight:600 }}>
                Pavyzdinis mygtukas
              </button>
            </div>
            <button className="btn btn-primary" type="submit" style={{ width:'100%', justifyContent:'center' }}>
              🎨 Išsaugoti spalvas
            </button>
          </form>
        </div>

        <div className="card">
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 20 }}>🔐 Keisti slaptažodį</div>
          <form onSubmit={handleChangePassword}>
            <div className="form-group">
              <label className="form-label">Dabartinis slaptažodis</label>
              <input className="form-input" type="password" value={pwForm.currentPassword} onChange={e => setPwForm({...pwForm, currentPassword: e.target.value})} required />
            </div>
            <div className="form-group">
              <PasswordInput label="Naujas slaptažodis" value={pwForm.newPassword} onChange={e => setPwForm({...pwForm, newPassword: e.target.value})} required />
            </div>
            <div className="form-group">
              <label className="form-label">Pakartoti naują slaptažodį</label>
              <input className="form-input" type="password" value={pwForm.confirm} onChange={e => setPwForm({...pwForm, confirm: e.target.value})} required />
            </div>
            <button className="btn btn-secondary" type="submit" style={{ width: '100%', justifyContent: 'center' }}>
              🔑 Pakeisti slaptažodį
            </button>
          </form>

          <div className="divider" />
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Paskyros informacija:</div>
            <div>📧 El. paštas: {user?.email}</div>
            <div>👤 Vardas: {user?.name}</div>
          </div>
        </div>
      </div>
    </div>
  );
}